import { useState } from 'react';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  CasBaseDialog,
  CasButton,
  CasChip,
  CasDisplayField,
  CasErrorDisplay,
  CasLoaderWithBackdrop,
  CasReactHookFormDialog,
  CasStackRow,
  CasTabsView,
} from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import Iconify from 'src/components/iconify';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { AccountsTab, OwnersTab, ContactsTab } from 'src/compass/application';
import { Terms } from 'src/compass/shared/util/terms';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';

import { CustomerSchema } from '../schema/CustomerSchema';
import CustomerFormFields from './CustomerFormFields';

type CustomerDialogProps = {
  id: number;
  onClose: VoidFunction;
}

export default function CustomerDialog({ id, onClose }: CustomerDialogProps) {
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);

  const { data, isFetching, isError, refetch } = useRetrieveDetailsById({
    entity: `Utilities/Entity`,
    id,
  });

  const { 
    data: touchpointData,
    isLoading: isTouchpointLoading,
    isRefetching: isTouchpointRefetching,
    isError: isErrorTouchpoint,
    refetch: refetchTouchpoint
  } = useRetrieveDetailsById({
    entity: 'Utilities/EntityTouchpoint',
    id,
  });

  const tabList = [
    { label: 'Accounts', children: <AccountsTab entityId={id} /> },
    { label: 'Owners', children: <OwnersTab entityId={id} /> },
    {
      label: 'Contacts',
      children: (
        <ContactsTab entityId={id} data={touchpointData?.contacts} onUpdated={refetchTouchpoint} />
      ),
    },
  ];

  const mutation = useUpdateMutation({
    url: 'Utilities/Entity',
    onSuccess: (successData) => {
      onSuccessEnqueueSnackbar(successData);
      setDialogRowId(null);
      refetch();
    },
  });

  const { mutate, isPending } = mutation;

  const saveEntity = (entityData: any) => {
    const { crmCompositeObj, ...rest } = entityData;
    mutate({ ...rest, crmCompositeId: crmCompositeObj?.id });
  };

  const queryClient = useQueryClient();

  const { mutate: clearHandoff } = useUpdateMutation({
    url: 'Utilities/EntityTouchpoint/ClearHandoff',
    showLoader: false,
    onSuccess: onSuccessEnqueueSnackbar,
  });

  // Optimistically toggle the handoff request flag on the cached Entity detail.
  const setHasHandoffRequest = (hasHandoffRequest: boolean) => {
    queryClient.setQueriesData(
      { queryKey: [`Utilities/Entity/${id}`] },
      (oldData: any) => (oldData ? { ...oldData, hasHandoffRequest } : oldData)
    );
  };

  const onMarkComplete = () => {
    setHasHandoffRequest(false); // optimistic
    clearHandoff({ entityId: id }, { onError: () => setHasHandoffRequest(true) });
  };

  if (isError || isErrorTouchpoint) return <CasErrorDisplay message="Error retrieving data from the server." />;

  return (
    <>
      <CasBaseDialog
        fullScreen
        maxWidth="xl"
        open
        onClose={onClose}
        title={
          data && (
            <CasStackRow alignItems="center">
              {`${Terms.Customer.label}: ${data?.name}`}
              <CasButton
                onClick={() => setDialogRowId(data?.id)}
                startIcon={<Iconify icon="mingcute:edit-line" />}
                key="edit"
              >
                Edit
              </CasButton>
            </CasStackRow>
          )
        }
        showCloseIcon
      >
        {isTouchpointRefetching && <CasLoaderWithBackdrop /> }
        <>
          {(isFetching || isTouchpointLoading) ? (
            <CasLoader />
          ) : (
            <>
              <CasDisplayField
                label={`${Terms.CRM.label} Name`}
                value={data?.crmCompositeObj?.name}
              />
              <CasStackRow>
                <CasDisplayField
                  label="Next Touchpoint"
                  value={formatDate(touchpointData?.nextTouchpointDate, DateFormat.MDY)}
                />
                <CasDisplayField label="Frequency" value={touchpointData.touchpointFrequency} />
                <CasDisplayField
                  label="Last Touchpoint"
                  value={formatDate(touchpointData?.lastTouchpointDate, DateFormat.MDY)}
                />
              </CasStackRow>
              <CasStackRow alignItems="center">
                <CasDisplayField label="RMC of Record" value={data?.rmcOfRecord} />
                {data?.hasHandoffRequest && (
                  <Box
                    sx={{
                      backgroundColor: '#a9ffa9',
                      width: 'fit-content',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    <CasStackRow alignItems="center">
                      {`Handoff req by ${data?.handoffRequestedByName}`}
                      {data?.handoffRequested && (
                        <Box component="span" sx={{ color: 'error.main', backgroundColor: 'yellow' }}>
                          &nbsp; Not changed yet &nbsp;
                        </Box>
                      )}
                      <CasLink onClick={onMarkComplete}>Mark complete</CasLink>
                    </CasStackRow>
                  </Box>
                )}
              </CasStackRow>
              <CasDisplayField
                label="Risk Managers"
                value={
                  <CasStackRow>
                    {touchpointData?.riskManagerNames?.map((riskManager: string) => (
                      <CasChip label={riskManager} color="disabled" />
                    ))}
                  </CasStackRow>
                }
              />
            </>
          )}
          <CasTabsView tabList={tabList} />
        </>
      </CasBaseDialog>
      {dialogRowId && (
        <CasReactHookFormDialog
          title={`Edit ${Terms.Customer.label}`}
          submitButtonLabel="Update"
          open
          onClose={() => setDialogRowId(null)}
          defaultValues={data}
          onSubmit={saveEntity}
          schema={CustomerSchema}
        >
          {isPending && <CasLoaderWithBackdrop />}
          <CustomerFormFields />
        </CasReactHookFormDialog>
      )}
    </>
  );
}
